
import { Vue, Component, Ref } from 'vue-property-decorator';

import Layout from '@/layouts/Default.vue';
import TitlePage from '@/components/Title.vue';

import CalendarRepository from '@/components/calendar/CalendarRepository.vue';
import CalendarRequests from '@/components/calendar/CalendarRequests.vue';
import RequestList from './components/RequestList.vue';
import RequestItemEdit from './components/RequestItemEdit.vue';

import ResponseHandlerModule from '@/store/modules/responseHandler';
import { getUnprocessedConsultations, confirmConsultationRequest, updateConsultationStatus } from '@/api/consultations';

import { addMinutes, discardTime } from '@/utils/date';

import { ConsultationRequest, ConsultationStatus, } from '@/types/consultations'
import { FreeSlot } from '@/components/calendar/types';
import { ISODateTimeRange } from '@/utils/date/types';
import { Maybe } from '@/types/common';
import { getList } from '@/api/crud';

const NO_DOCTOR = 0;

@Component({
    components: {
        Layout,
        TitlePage,
        CalendarRepository,
        CalendarRequests,
        RequestList,
        RequestItemEdit,
    }
})
export default class RequestPage extends Vue {
    @Ref() $calendar!:  CalendarRequests;

    requests: ConsultationRequest[] = []
    selectedRequest: Maybe<ConsultationRequest> = null;

    selectedSlotRange: Maybe<ISODateTimeRange> = null;
    requestDoctorId = NO_DOCTOR;
    types = []

    get hasDoctor() {
        return this.requestDoctorId !== NO_DOCTOR;
    }

    async created() {
        try {
            this.requests = await getUnprocessedConsultations();
            this.setTypes();
        } catch (error) {
            console.log(error);
        }
    }

    async setTypes() {
        const result = await getList('medical-service-types', { page: 1 });

        this.types = result.table.rows;
    }

    findTypeId(type: string): { id: number } | undefined {
        return this.types.find((item: { title: string }) => { 
            return type === item.title && (item.title === 'Срочная онлайн-консультация' || item.title === 'Консультация на дому');
        });
    }

    selectSlot(slot: Maybe<FreeSlot>) {
        if (slot) {
            const duration = this.selectedRequest?.duration ?? 15;
            const end = addMinutes(slot.start, duration);

            const time = {
                start: slot.start,
                end,
            };

            this.selectedSlotRange = time;

            if (this.selectedRequest) {
                this.selectedRequest = {
                    ...this.selectedRequest,
                    time,
                }
            }

            return;
        }

        this.selectedSlotRange = null;
        if (this.selectedRequest) {
            this.selectedRequest = {
                ...this.selectedRequest,
                time: undefined,
            }
        }
    }

    updateDoctor(doctorId: number) {
        this.requestDoctorId = doctorId;
    }

    updateDuration(duration: number) {
        if (this.selectedSlotRange) {
            this.selectedSlotRange.end = addMinutes(this.selectedSlotRange.start, duration);
        }
    }

    selectRequest(request: ConsultationRequest) {
        this.selectedRequest = {
            ...request
        };

        if (request.doctor) {
            this.requestDoctorId = request.doctor.id;
        }

        if (request.time) {
            this.selectedSlotRange = request.time;

            const date = discardTime(request.time.start);

            this.$calendar.setDay(date);
        }
    }

    cancelRequestEdit() {
        this.selectedRequest = null;
        this.selectedSlotRange = null;
        this.requestDoctorId = NO_DOCTOR;
    }

    removeRequestFromList(requestToRemove: ConsultationRequest) {
        this.requests = this.requests.filter(request => request.id !== requestToRemove.id);
    }

    async confirmRequest(request: ConsultationRequest) {
        try {
            const data = request;

            // отключено в рамках задачи https://youtrack.mobecan.com/issue/ALFAMED-278 да и вообще странно запрашивать цену, если мы в поле указали ее
            // const type = this.findTypeId(request.type);
            //
            // if (type) {
            //     const price = await getConsultationCalculated(type?.id, request?.doctor?.id as number, request.specialization.id, request.patient.id);
            //
            //     if (price) {
            //         data.price = price;
            //     }
            // }

            await confirmConsultationRequest(data);
            this.cancelRequestEdit();
            await this.$nextTick();
            this.removeRequestFromList(data);

            ResponseHandlerModule.showNotify({ message: 'Консультация подтверждена', type: 'ok' }); 
        } catch (error: any) {
           ResponseHandlerModule.showNotify({ message: error.message, type: 'fail' });
        }
    }

    async updateStatus({ request, status }: { request: ConsultationRequest; status: ConsultationStatus }) {
        try {
            await updateConsultationStatus(request.id, status);

            this.removeRequestFromList(request);

            ResponseHandlerModule.showNotify({ message: 'Заявка отменена', type: 'ok' }); 
        } catch (error) {
            ResponseHandlerModule.showNotify({ message: error.message, type: 'fail' });
        }
    }
}
